// ================================================================
// Unified Role System Constants
// Updated: 2026-09-14 - Removed manager (issue #4856). The owner deprecated it,
//   the database rejects it via the same allowlist CHECK, and production has
//   zero rows carrying it.
// Updated: 2026-08-02 - Removed general_manager and program_director (issue
//   #2856). Both were deprecated by the owner; neither has ever existed in
//   org_staff_roles, and the database now rejects them outright via the
//   org_staff_roles_role_name_allowlist CHECK constraint.
// Updated: 2026-06-03 - Added DISABLED_ORG_ROLES_FOR_LAUNCH as the single source
//   of truth for the launch-disabled org roles (issue #637 FIX D). The grant API
//   route and the Add-Staff UI both import it so a direct API caller cannot grant
//   a role the UI hides.
// Updated: 2026-06-02 - Promoted canonical getDefaultRolePermissions + added
//   STAFF_GRANT_ROLES here as the single source of truth (plan
//   docs/plans/2026-06-02-001-feat-grant-staff-access-existing-parent-plan.md,
//   issue #637, KTD2/KTD3). De-forks the duplicated getDefaultRolePermissions
//   switch that previously lived privately in staff-invitations/accept and
//   staff-invitations/register routes.
// Updated: 2025-10-17 - Added general_manager, synced with database
// Updated: 2025-07-20 - Added PRD-aligned roles with hybrid approach
// Previous: 2025-06-05 - Resolves role conflicts between org and team levels
// ================================================================

/**
 * ⚠️ IMPORTANT: DATABASE & NAVIGATION SYNCHRONIZATION REQUIRED ⚠️
 *
 * When adding a new role to this file, you MUST:
 * 1. Update the code constants
 * 2. Add to the database
 * 3. Update role groupings below
 * 4. Navigation will automatically inherit from role groupings!
 *
 * Steps to add a new role:
 * 1. Add the role constant here in ORG_ROLES
 * 2. Add display name to ORG_ROLE_DISPLAY_NAMES
 * 3. Add description to ORG_ROLE_DESCRIPTIONS
 * 4. Add hierarchy level to ORG_ROLE_HIERARCHY
 * 5. Add default permissions to DEFAULT_ORG_PERMISSIONS
 * 6. Add to appropriate role groupings below (MANAGEMENT_ROLES, COACHING_ROLES, etc.)
 *    → This ensures navigation automatically includes the new role!
 * 7. Create a database migration to add the role to org_staff_roles table
 *    - Use migration pattern: INSERT INTO org_staff_roles for ALL organizations
 *    - Include: role_name, display_name, description, hierarchy_level, permissions
 *    - Set is_system_role = true
 *
 * Database table: public.org_staff_roles
 * Migration location: /supabase/migrations/
 * Navigation file: /src/config/navigation.tsx (uses role groupings)
 *
 * ⚠️ With role groupings, navigation updates automatically when you add roles to groups! ⚠️
 */

/**
 * Organization-Level Roles (Capabilities)
 * Define what a user CAN do within the organization
 *
 * HYBRID APPROACH:
 * - Common roles (front_desk, instructor) have pre-defined permissions
 * - Custom needs use read_only_staff with configurable permissions
 * - Includes all PRD-specified roles for full compliance
 *
 * ⚠️ LAUNCH SIMPLIFICATION (2025-12-31):
 * The following roles are DISABLED for new staff creation to simplify initial launch:
 * - OFFICE_ADMIN: Use FRONT_DESK instead
 * - INSTRUCTOR: Use COACH instead
 *
 * These roles remain functional for existing staff but cannot be assigned to new staff.
 * They are marked as is_active=false in the database (migration: 20251231_001_disable_roles_for_launch.sql)
 *
 * GENERAL_MANAGER and PROGRAM_DIRECTOR (issue #2856) and MANAGER (issue #4856)
 * were also on that list and are now GONE. No gym has any of them, and the database rejects all three:
 * org_staff_roles.role_name is pinned to the seven live roles by the
 * org_staff_roles_role_name_allowlist CHECK constraint. Do not re-add them here
 * without adding them to that constraint in the same PR — see
 * ORG_STAFF_ROLE_NAMES below.
 */
export const ORG_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  COACH: 'coach',
  OFFICE_ADMIN: 'office_admin', // ⚠️ DISABLED for launch - Use FRONT_DESK
  FRONT_DESK: 'front_desk',
  INSTRUCTOR: 'instructor', // ⚠️ DISABLED for launch - Use COACH
  READ_ONLY_STAFF: 'read_only_staff'
} as const;

export type OrgRole = typeof ORG_ROLES[keyof typeof ORG_ROLES];

/**
 * Legacy org-level role_names that are storable but are not canonical ORG_ROLES
 * values. Role gates that compare against the RAW role_name — `requireStaffRole`
 * reads `org_staff_roles.role_name` directly — accept these too, which is what
 * `utils/permissions/reportsAccess.ts` uses this type for.
 *
 * ⚠️ CORRECTED 2026-08-31. This said they "exist on real prod org_staff_roles
 * rows". They do not, and never did: a read-only production census (with known
 * positives — admin 33 rows all outside the demo org, owner 81 of which 80
 * are outside, the 81st necessarily being the demo gym's own) found exactly
 * one row each for head_coach, assistant_coach and coordinator, ALL of them in
 * CheerVue Demo Gym, and the demo seeder is what created them. That seeder now
 * seeds canonical names, so after the next weekly reseed these have zero rows
 * anywhere. The old sentence is what sent one session building a branch to
 * teach permission resolution to honour them; it was dropped. Do not restore
 * the claim without re-running the census. (`coordinator` has since been
 * removed from the database allowlist entirely — #5920.)
 */
export type LegacyOrgRoleName = 'head_coach' | 'assistant_coach';
export type OrgRoleName = OrgRole | LegacyOrgRoleName;

/**
 * Every `org_staff_roles.role_name` the database will accept (issue #2856).
 *
 * This MIRRORS the `org_staff_roles_role_name_allowlist` CHECK constraint added
 * in migration `20260802_004_org_staff_roles_role_name_allowlist.sql` and last
 * re-created in `20261250_001_drop_coordinator_staff_role.sql`. Anything
 * outside this list is rejected by Postgres, so any route that writes a
 * role_name should check against this first and return a clean message rather
 * than letting the insert fail with a raw database error.
 *
 * It is deliberately WIDER than ORG_ROLES minus the launch-disabled names: it
 * also includes head_coach and assistant_coach, because the database CHECK
 * constraint accepts them. (It was previously said they "only ever existed as
 * real prod rows" — corrected 2026-08-31: they existed only in the demo gym's
 * seed data. The list must still mirror the constraint, so they stay here;
 * removing them means changing the constraint too.)
 *
 * `coordinator` used to be on this list. It came only from demo-gym seed data
 * (commit 1ae4b1028e); the demo seeder stopped creating it in commit 54dce5ef99
 * (#4244). Migration `20261250_001_drop_coordinator_staff_role.sql` removed it
 * from the constraint (#5920), so it is gone from here too.
 *
 * Adding a role means changing BOTH this list and the constraint, in the same PR.
 */
export const ORG_STAFF_ROLE_NAMES = [
  'owner',
  'admin',
  'coach',
  'head_coach',
  'assistant_coach',
  'front_desk',
  'read_only_staff',
] as const;

export type OrgStaffRoleName = typeof ORG_STAFF_ROLE_NAMES[number];

/** True when `roleName` is a role_name the database allowlist accepts. */
export function isStorableOrgStaffRoleName(roleName: string): boolean {
  return (ORG_STAFF_ROLE_NAMES as readonly string[]).includes(
    (roleName || '').trim().toLowerCase()
  );
}

/**
 * Team-Level Roles (Specific Assignments)
 * Define what a user IS doing on specific teams
 * Prefixed with 'team_' to prevent conflicts with org roles
 */
export const TEAM_ROLES = {
  HEAD_COACH: 'team_head_coach',
  ASSISTANT_COACH: 'team_assistant_coach',
  PARENT_REPRESENTATIVE: 'team_parent_representative'
} as const;

export type TeamRole = typeof TEAM_ROLES[keyof typeof TEAM_ROLES];

/**
 * Display names for organization roles
 */
export const ORG_ROLE_DISPLAY_NAMES: Record<OrgRole, string> = {
  [ORG_ROLES.OWNER]: 'Owner',
  [ORG_ROLES.ADMIN]: 'Administrator',
  [ORG_ROLES.COACH]: 'Coach',
  [ORG_ROLES.OFFICE_ADMIN]: 'Office Administrator',
  [ORG_ROLES.FRONT_DESK]: 'Front Desk',
  [ORG_ROLES.INSTRUCTOR]: 'Instructor',
  [ORG_ROLES.READ_ONLY_STAFF]: 'Staff Member'
};

/**
 * Display names for team roles
 */
export const TEAM_ROLE_DISPLAY_NAMES: Record<TeamRole, string> = {
  [TEAM_ROLES.HEAD_COACH]: 'Head Coach',
  [TEAM_ROLES.ASSISTANT_COACH]: 'Assistant Coach',
  [TEAM_ROLES.PARENT_REPRESENTATIVE]: 'Parent Representative'
};

/**
 * Descriptions for team roles
 */
export const TEAM_ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  [TEAM_ROLES.HEAD_COACH]: 'Primary coach responsible for team leadership and strategy',
  [TEAM_ROLES.ASSISTANT_COACH]: 'Supporting coach who assists with team training and development',
  [TEAM_ROLES.PARENT_REPRESENTATIVE]: 'Parent liaison who helps with communication and team coordination'
};

/**
 * Organization Role Descriptions
 * Explains what each role can do and their responsibilities
 */
export const ORG_ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  [ORG_ROLES.OWNER]: "Complete access to all features including billing, staff management, and business operations. Can manage everything in the organization.",
  [ORG_ROLES.ADMIN]: "Full access, including billing and finance.",
  [ORG_ROLES.COACH]: "Manages teams, athletes, and evaluations.",
  [ORG_ROLES.OFFICE_ADMIN]: "Handles administrative tasks including staff management, athlete records, billing, and reports. Cannot manage teams or locations.",
  [ORG_ROLES.INSTRUCTOR]: "Can manage athlete information and records. Typically used for instructors who work with individual athletes but don't manage teams.",
  [ORG_ROLES.FRONT_DESK]: "Handles check-ins and point-of-sale.",
  [ORG_ROLES.READ_ONLY_STAFF]: "View-only access to reports."
};

/**
 * Role hierarchy levels (higher number = more permissions)
 * These should match the org_staff_roles.hierarchy_level in the database
 */
export const ORG_ROLE_HIERARCHY: Record<OrgRole, number> = {
  [ORG_ROLES.OWNER]: 100,
  [ORG_ROLES.ADMIN]: 90,
  [ORG_ROLES.OFFICE_ADMIN]: 60,
  [ORG_ROLES.COACH]: 50,
  [ORG_ROLES.INSTRUCTOR]: 40,
  [ORG_ROLES.FRONT_DESK]: 30,
  [ORG_ROLES.READ_ONLY_STAFF]: 20
};

/**
 * Role Groupings for Navigation and Permissions
 * These logical groups make it easier to assign roles to features
 */

// All organization roles
export const ALL_ORG_ROLES: OrgRole[] = Object.values(ORG_ROLES);

// Full access roles (equivalent permissions to Owner)
export const FULL_ACCESS_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Senior leadership roles (full management capabilities)
export const SENIOR_LEADERSHIP_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// All management roles (can manage staff/teams/athletes)
export const MANAGEMENT_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Roles allowed to PERMANENTLY delete an athlete profile. Deletion is irreversible
// and cascades the athlete's connected records, so it is restricted to owner/admin
// and shared by the detail-page gate and the staff-delete API route (#1195).
export const ATHLETE_DELETE_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Roles allowed to create or change an athlete discount, and a class's team
// athlete discount (#5131 KTD14). Narrower than BILLING_ACCESS_ROLES on purpose:
// office admins and front desk can view discounts but not set them.
export const ATHLETE_DISCOUNT_WRITE_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Coaching roles (work with teams and athletes)
export const COACHING_ROLES: OrgRole[] = [
  ORG_ROLES.COACH,
];

// Roles that can be assigned as a coach for teams, classes, and private lessons
// Includes admin for smaller gyms where owners/admins share coaching duties
export const COACH_ASSIGNABLE_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.COACH,
];

// Administrative support roles
export const ADMIN_SUPPORT_ROLES: OrgRole[] = [
  ORG_ROLES.OFFICE_ADMIN,
  ORG_ROLES.FRONT_DESK
];

// Roles that can manage teams
export const TEAM_MANAGEMENT_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.COACH
];

// Roles allowed to EDIT a family's travel booking (correct an AI mis-parse) from
// the staff Travel & Logistics view (issue #1212): TEAM_MANAGEMENT_ROLES WITHOUT
// coach. A coach is evaluation-only (the #978 model — they manage teams and
// evaluations, not family PII/logistics data), so they can VIEW travel but not
// edit a family's booking. Derived from TEAM_MANAGEMENT_ROLES (not re-listed) so
// it can't drift if that set changes. Used by both the staff edit API route's
// role gate and the page's edit-pencil gate so the two never disagree.
export const TRAVEL_BOOKING_EDIT_ROLES: OrgRole[] = TEAM_MANAGEMENT_ROLES.filter(
  (role) => role !== ORG_ROLES.COACH
);

// Roles that can manage athletes.
// COACH intentionally EXCLUDED (issue #978): coaches were given gym-wide
// read/evaluation access in PR #470 and are evaluation-only — they do NOT manage
// (edit) athlete profiles/PII. The data layer enforces this via RLS
// (fga_staff_can_manage_athlete) + the per-org org_staff_roles.can_manage_athletes
// flag; this constant is kept honest so a future reader isn't misled. #471 will make
// coach athlete-edit access per-org configurable.
export const ATHLETE_MANAGEMENT_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.OFFICE_ADMIN,
];

// Roles that can manage billing (includes front desk for payments, invoices, POS).
// Also the allow-list for private-lesson refunds and credit give-backs (#6237).
// A new role that must not touch money (the planned Director, #6135) stays OUT.
export const BILLING_ACCESS_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.OFFICE_ADMIN,
  ORG_ROLES.FRONT_DESK
];

// Roles that can manage staff
export const STAFF_MANAGEMENT_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.OFFICE_ADMIN
];

// Roles that can create staff (higher level)
export const STAFF_CREATION_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Roles that can set/reset/unlock kiosk PINs for staff (Time Log feature).
// Front desk needs this so they can recover a coach's locked-out PIN at 6 AM
// when no owner is on site. STAFF_MANAGEMENT_ROLES + FRONT_DESK.
export const PIN_MANAGER_ROLES: OrgRole[] = [
  ...STAFF_MANAGEMENT_ROLES,
  ORG_ROLES.FRONT_DESK
];

// Roles that can offer waitlist spots to families. Intentionally restrictive —
// offering a spot triggers a customer-facing email and binds a claim
// window, so it's gated to owner/admin only. Display this list in the staff
// waitlist UI so other staff understand why the action is hidden for them.
export const WAITLIST_OFFER_ROLES: OrgRole[] = [
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN
];

// Requirements summary: Gym Calendar operations visibility is intentionally
// limited to full-access leadership plus front desk for day-of support.
export const CALENDAR_OPERATIONS_VISIBILITY_ROLES: OrgRole[] = [
  ...FULL_ACCESS_ROLES,
  ORG_ROLES.FRONT_DESK
];

// Requirements summary: Front desk can see the full operations calendar, but
// event management stays with roles that already manage team schedules.
export const CALENDAR_EVENT_MANAGEMENT_ROLES: OrgRole[] = [
  ...MANAGEMENT_ROLES,
  ...COACHING_ROLES
];

// Roles allowed to set/change/clear an athlete's event RSVP on a family's behalf
// (issue #1391): CALENDAR_EVENT_MANAGEMENT_ROLES WITHOUT coach. Coaches are
// evaluation-only (the #978 model) — they can SEE who hasn't responded but must
// not answer for a family. Derived from CALENDAR_EVENT_MANAGEMENT_ROLES (not
// re-listed) so it can't drift if that set changes, mirroring
// TRAVEL_BOOKING_EDIT_ROLES above. Imported by BOTH the RSVP API route's role
// gate and the details-modal edit-controls gate so the two never disagree.
// (Single line: this file sits at its eslint max-lines cap.)
export const EVENT_RSVP_MANAGEMENT_ROLES: OrgRole[] = CALENDAR_EVENT_MANAGEMENT_ROLES.filter((role) => role !== ORG_ROLES.COACH);

// Walk-in punch redemption spends prepaid family value — every role here is a
// deliberate, reviewed grant. Add roles in the PR that actually needs them, never
// speculatively. EXPLICIT enumeration — intentionally NOT derived from (or mirroring)
// BILLING_ACCESS_ROLES, so a change to billing access can never silently widen who
// may spend a family's prepaid Open Gym visits. Imported by every walk-in surface's
// role gate (dashboard route, kiosk route, assistant tool) so they never disagree.
// (Single line: this file sits at its eslint max-lines cap.)
export const OPEN_GYM_WALKIN_REDEEM_ROLES: OrgRole[] = [ORG_ROLES.OWNER, ORG_ROLES.ADMIN, ORG_ROLES.FRONT_DESK];

// Staff Bulk Collection "Collect outstanding balances" (#2041): triggers immediate card
// charges across many families, so it is owner/admin ONLY. Mass card charges must NEVER
// gate on the wider BILLING_ACCESS_ROLES (front_desk/office_admin/etc. run day-to-day
// billing screens but must not be able to charge every family's saved card). Imported by
// the collect preview/execute routes and the assistant action tool so the gates never
// disagree. (Compact block: this file sits at its eslint max-lines cap.)
export const STAFF_BULK_COLLECT_ROLES = ['owner', 'admin'] as const;

/**
 * Helper functions for role management
 */
export const RoleUtils = {
  /**
   * Get display name for organization role
   */
  getOrgRoleDisplayName: (role: string): string => {
    return ORG_ROLE_DISPLAY_NAMES[role as OrgRole] || role;
  },

  /**
   * Get display name for team role
   */
  getTeamRoleDisplayName: (role: string): string => {
    return TEAM_ROLE_DISPLAY_NAMES[role as TeamRole] || role;
  },

  /**
   * Get description for team role
   */
  getTeamRoleDescription: (role: string): string => {
    return TEAM_ROLE_DESCRIPTIONS[role as TeamRole] || '';
  },

  /**
   * Get description for organization role
   */
  getOrgRoleDescription: (role: string): string => {
    return ORG_ROLE_DESCRIPTIONS[role as OrgRole] || '';
  },

  /**
   * Check if role is an organization role
   */
  isOrgRole: (role: string): role is OrgRole => {
    return Object.values(ORG_ROLES).includes(role as OrgRole);
  },

  /**
   * Check if role is a team role
   */
  isTeamRole: (role: string): role is TeamRole => {
    return Object.values(TEAM_ROLES).includes(role as TeamRole);
  },

  /**
   * Get hierarchy level for organization role
   */
  getOrgRoleHierarchy: (role: string): number => {
    return ORG_ROLE_HIERARCHY[role as OrgRole] || 0;
  },

  /**
   * Check if one org role outranks another
   */
  doesRoleOutrank: (higherRole: string, lowerRole: string): boolean => {
    return RoleUtils.getOrgRoleHierarchy(higherRole) > RoleUtils.getOrgRoleHierarchy(lowerRole);
  },

  /**
   * Get all org roles as array for forms/dropdowns
   */
  getOrgRoleOptions: () => {
    return Object.values(ORG_ROLES).map(role => ({
      value: role,
      label: ORG_ROLE_DISPLAY_NAMES[role],
      description: ORG_ROLE_DESCRIPTIONS[role],
      hierarchy: ORG_ROLE_HIERARCHY[role]
    })).sort((a, b) => b.hierarchy - a.hierarchy); // Sort by hierarchy desc
  },

  /**
   * Get all team roles as array for forms/dropdowns
   */
  getTeamRoleOptions: () => {
    return Object.values(TEAM_ROLES).map(role => ({
      value: role,
      label: TEAM_ROLE_DISPLAY_NAMES[role],
      description: TEAM_ROLE_DESCRIPTIONS[role]
    }));
  }
};

/**
 * Default permissions by organization role
 * These should match the org_staff_roles table permissions
 */
// Keyed by canonical OrgRole values PLUS the legacy literal role_names
// 'head_coach' / 'assistant_coach'. See the correction on LegacyOrgRoleName
// above: those names live only in demo seed data, and the parenthetical that
// used to be here — "UserRoleContext looks up by raw role_name" — is wrong.
// UserRoleContext.tsx:689 filters through RoleUtils.isOrgRole FIRST, so these
// two keys are unreachable from that path.
export const DEFAULT_ORG_PERMISSIONS: Record<OrgRole | 'head_coach' | 'assistant_coach', {
  can_manage_staff: boolean;
  can_manage_teams: boolean;
  can_manage_athletes: boolean;
  can_manage_billing: boolean;
  can_manage_locations: boolean;
  can_view_reports: boolean;
  can_send_notifications: boolean;
  can_manage_notifications: boolean;
  can_manage_notification_templates: boolean;
}> = {
  [ORG_ROLES.OWNER]: {
    can_manage_staff: true,
    can_manage_teams: true,
    can_manage_athletes: true,
    can_manage_billing: true,
    can_manage_locations: true,
    can_view_reports: true,
    can_send_notifications: true,
    can_manage_notifications: true,
    can_manage_notification_templates: true
  },
  [ORG_ROLES.ADMIN]: {
    can_manage_staff: true,
    can_manage_teams: true,
    can_manage_athletes: true,
    can_manage_billing: true,
    can_manage_locations: true,
    can_view_reports: true,
    can_send_notifications: true,
    can_manage_notifications: true,
    can_manage_notification_templates: true
  },
  [ORG_ROLES.COACH]: {
    can_manage_staff: false,
    can_manage_teams: true,
    // Coaches are evaluation-only (PR #470 gym-wide read model); they do NOT manage
    // (edit) athlete profiles/PII (issue #978). RLS + the per-org
    // org_staff_roles.can_manage_athletes flag are authoritative; this default is
    // aligned so it no longer claims an ability the data layer denies. #471 will make
    // this per-org configurable.
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: true,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  },
  // Coach comms unification (U5). ⚠️ TWO CORRECTIONS, 2026-08-31, both verified:
  // (1) these role_names were never on real prod rows — only demo seed data,
  // now itself canonical; (2) "UserRoleContext looks permissions up by RAW
  // role_name" is FALSE — UserRoleContext.tsx:689 filters through
  // RoleUtils.isOrgRole first, so these keys never resolve from that path and
  // the lock-out they were added to prevent happened anyway.
  // They are kept, not deleted, because gates that read the raw role_name
  // (requireStaffRole → reportsAccess) legitimately still see these names while
  // the database allowlist accepts them. Deleting them is its own change.
  // Mirror the COACH entry; assistant_coach keeps the #978 read-only athlete
  // model (can_manage_athletes stays false for both, same as COACH).
  head_coach: {
    can_manage_staff: false,
    can_manage_teams: true,
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: true,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  },
  assistant_coach: {
    can_manage_staff: false,
    can_manage_teams: false,
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: true,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  },
  [ORG_ROLES.OFFICE_ADMIN]: {
    can_manage_staff: true,
    can_manage_teams: false,
    can_manage_athletes: true,
    can_manage_billing: true,
    can_manage_locations: false,
    can_view_reports: true,
    can_send_notifications: true,
    can_manage_notifications: true,
    can_manage_notification_templates: false
  },
  [ORG_ROLES.INSTRUCTOR]: {
    can_manage_staff: false,
    can_manage_teams: false,
    // Instructor is a coaching-style role (disabled for launch — use COACH). Aligned
    // with the coach evaluation-only model (issue #978): no athlete-profile management
    // by default. RLS + the per-org can_manage_athletes flag remain authoritative.
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: true,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  },
  [ORG_ROLES.FRONT_DESK]: {
    can_manage_staff: false,
    can_manage_teams: false,
    can_manage_athletes: false,
    can_manage_billing: true, // Access Billing section: payments, invoices, POS
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: false,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  },
  [ORG_ROLES.READ_ONLY_STAFF]: {
    can_manage_staff: false,
    can_manage_teams: false,
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: true,
    can_send_notifications: false,
    can_manage_notifications: false,
    can_manage_notification_templates: false
  }
};

/**
 * Roles allowed to GRANT staff access to an existing parent account
 * (issue #637 / KTD3). Intentionally NARROWER than STAFF_MANAGEMENT_ROLES,
 * which also includes office_admin —
 * granting (including granting Admin) is owner/admin only. Single source of
 * truth: both the grant route (U3) and the org parent-roster route (U4) import
 * this so the allow-list never drifts between them.
 *
 * Because this allow-list already rejects every caller below admin level, the
 * grant route does NOT need the standalone "minimum caller level" short-circuit
 * that admin/users/create/route.ts carries (that route uses the broad
 * STAFF_MANAGEMENT_ROLES constant); the per-assignment hierarchy guard is the
 * only additional caller check the grant route needs.
 */
export const STAFF_GRANT_ROLES = ['owner', 'admin'] as const;

export type StaffGrantRole = typeof STAFF_GRANT_ROLES[number];

/**
 * Org roles that are DISABLED for new staff creation at launch (see the
 * "LAUNCH SIMPLIFICATION" note on ORG_ROLES above and migration
 * 20251231_001_disable_roles_for_launch.sql). These roles remain valid for
 * EXISTING staff but must not be assignable to anyone new — the Add-Staff UI
 * already filters them out of its role picker.
 *
 * general_manager and program_director (#2856) and manager (#4856) are NOT here
 * any more: they were removed from ORG_ROLES entirely, so they are rejected earlier and harder — by
 * RoleUtils.isOrgRole, by isStorableOrgStaffRoleName, and by the database
 * constraint — rather than by this list.
 *
 * SINGLE SOURCE OF TRUTH (issue #637 FIX D): both the grant API route and the
 * Add-Staff UI import this list so a direct API caller cannot grant a role the
 * UI hides. Keep it in sync with the ⚠️-flagged entries in ORG_ROLES.
 */
export const DISABLED_ORG_ROLES_FOR_LAUNCH = [
  'office_admin',
  'instructor',
] as const;

export type DisabledOrgRoleForLaunch = typeof DISABLED_ORG_ROLES_FOR_LAUNCH[number];

/**
 * Shape of the role defaults written into an `org_staff_roles` row when a role
 * is auto-created (during staff invite-accept, registration, or an existing-parent
 * staff grant). NOTE: this is intentionally the 6 capability flags + hierarchy_level
 * that the `org_staff_roles` table actually stores — it does NOT include the three
 * notification flags from DEFAULT_ORG_PERMISSIONS (can_send_notifications,
 * can_manage_notifications, can_manage_notification_templates), because those
 * columns do not exist on org_staff_roles. Keeping this shape exact preserves the
 * historical insert payload of the accept/register routes.
 */
export interface OrgStaffRoleDefaults {
  hierarchy_level: number;
  can_manage_staff: boolean;
  can_manage_teams: boolean;
  can_manage_athletes: boolean;
  can_manage_billing: boolean;
  can_manage_locations: boolean;
  can_view_reports: boolean;
}

/**
 * Canonical default permission flags for auto-creating an `org_staff_roles` row
 * from a role NAME string (issue #637 / KTD2). This is the SINGLE authoritative
 * home for this logic; previously it was a private, duplicated switch in
 * staff-invitations/accept/route.ts and staff-invitations/register/route.ts, and
 * a parallel typed map (DEFAULT_ORG_PERMISSIONS) lived here. This function is the
 * one place all three converge — do not re-fork it inside services or routes.
 *
 * Reconciliation with DEFAULT_ORG_PERMISSIONS + ORG_ROLE_HIERARCHY:
 * - For every canonical role those structures define, the returned values are
 *   IDENTICAL on the six flags org_staff_roles stores (verified row-by-row), so
 *   for canonical role names we derive directly from them. This is purely a
 *   de-fork; it does not change any value the old switch produced.
 * - This function additionally accepts role-name ALIASES the old switch handled
 *   ('administrator' -> admin, 'head_coach' -> coach) and a legacy
 *   'assistant_coach' role that is not in ORG_ROLES, because real org_staff_roles
 *   rows / invitations can carry those strings.
 * - For an UNKNOWN role name it returns a deliberately restrictive fallback
 *   (hierarchy_level 10, all capability flags false) — matching the old switch's
 *   `default` branch exactly. This is intentionally MORE restrictive than
 *   read_only_staff; do not "upgrade" it to a canonical role's permissions.
 *
 * Output shape is OrgStaffRoleDefaults (7 keys) so it can be spread straight into
 * an org_staff_roles insert exactly as the routes did before.
 */
export function getDefaultRolePermissions(roleName: string): OrgStaffRoleDefaults {
  const normalized = (roleName || '').toLowerCase();

  // Pick from the canonical permission/hierarchy maps for known canonical roles,
  // narrowing DEFAULT_ORG_PERMISSIONS (9 flags) down to the 6 org_staff_roles stores.
  const fromCanonical = (role: OrgRole): OrgStaffRoleDefaults => {
    const p = DEFAULT_ORG_PERMISSIONS[role];
    return {
      hierarchy_level: ORG_ROLE_HIERARCHY[role],
      can_manage_staff: p.can_manage_staff,
      can_manage_teams: p.can_manage_teams,
      can_manage_athletes: p.can_manage_athletes,
      can_manage_billing: p.can_manage_billing,
      can_manage_locations: p.can_manage_locations,
      can_view_reports: p.can_view_reports,
    };
  };

  switch (normalized) {
    case 'owner':
      return fromCanonical(ORG_ROLES.OWNER);
    case 'admin':
    case 'administrator': // alias preserved from the original forked switch
      return fromCanonical(ORG_ROLES.ADMIN);
    case 'office_admin':
      return fromCanonical(ORG_ROLES.OFFICE_ADMIN);
    case 'coach':
    case 'head_coach': // alias preserved from the original forked switch
      return fromCanonical(ORG_ROLES.COACH);
    case 'instructor':
      return fromCanonical(ORG_ROLES.INSTRUCTOR);
    case 'front_desk':
      return fromCanonical(ORG_ROLES.FRONT_DESK);
    case 'read_only_staff':
      return fromCanonical(ORG_ROLES.READ_ONLY_STAFF);
    case 'assistant_coach':
      // Legacy role string not present in ORG_ROLES. Issue #978: assistant_coach is a
      // coaching role and is evaluation-only — it does NOT manage (edit) athlete PII.
      // This is the seed used when auto-creating an org_staff_roles row from an
      // invitation, so flipping can_manage_athletes to false here keeps NEWLY invited
      // assistant coaches consistent with the read-only model and the prod backfill
      // (otherwise a new invite would re-open the hole the migration closes).
      return {
        hierarchy_level: 30,
        can_manage_staff: false,
        can_manage_teams: false,
        can_manage_athletes: false,
        can_manage_billing: false,
        can_manage_locations: false,
        can_view_reports: false,
      };
    default:
      // Unknown role → most restrictive defaults (matches old switch `default`).
      return {
        hierarchy_level: 10,
        can_manage_staff: false,
        can_manage_teams: false,
        can_manage_athletes: false,
        can_manage_billing: false,
        can_manage_locations: false,
        can_view_reports: false,
      };
  }
}
